import { Seo } from '../components/academy/Seo';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronUp } from 'lucide-react';

const FAQ = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What areas do you service?",
      answer: "CarLens currently services North Florida and South Georgia. This includes Jacksonville, St. Augustine, Gainesville, and surrounding areas in Florida, as well as Valdosta, Brunswick, and nearby cities in Georgia."
    },
    {
      question: "How long does an inspection take?",
      answer: "A CarLens Certified Pre-Purchase Inspection takes approximately 1-2 hours to complete, depending on the vehicle and diagnostic tests required. You'll receive your detailed Vehicle Decision Report™ shortly after the technician completes the inspection."
    },
    {
      question: "What does the inspection include?",
      answer: "We offer two inspections. The CarLens Certified Pre-Purchase Inspection (for cars, trucks & SUVs) is $199. The CarLens Exotic Inspection (for exotic, luxury & high-performance vehicles) is $299. The inspection price includes the inspection and written report; a live report review and consultation with your inspector is an additional $29. Both are comprehensive 200+ point inspections that include your Vehicle Decision Report™ with a CarLens Confidence Score™, an executive summary, ownership guidance, a 24-month ownership cost forecast, detailed photo evidence, diagnostic scan results, and CarLens Vehicle Certification. Optional add-ons include a Carfax vehicle history report (+$59.99), Negotiation Guidance (+$49), and Priority Same-Day Service (+$49, automatically added for same-day appointments)."
    },
    {
      question: "Can I be present during the inspection?",
      answer: "Yes! We encourage customers to be present during the inspection if possible. Our inspectors will walk you through their findings and answer any questions you may have about the vehicle's condition."
    },
    {
      question: "What if the inspection reveals major issues?",
      answer: "Our detailed report will clearly identify any issues found, categorized by severity. You can use this information to request repairs, plan for upcoming maintenance and ownership costs, or decide to walk away from the purchase. Our goal is to give you the truth before you buy."
    },
    {
      question: "Do you inspect all types of vehicles?",
      answer: "We inspect most passenger vehicles including cars, trucks, SUVs, and vans—from everyday commuters to exotic and high-value vehicles. We do not currently inspect motorcycles, RVs, or commercial vehicles."
    },
    {
      question: "How do I schedule an inspection?",
      answer: "Simply click 'Book Inspection Now' on our website, fill in the vehicle details and your contact information, and choose your preferred date and time. You'll need to create an account to complete the booking. We'll confirm your appointment via email."
    },
    {
      question: "What payment methods do you accept?",
      answer: "We accept payments through PayPal, which allows you to pay with credit cards, debit cards, or your PayPal balance. Payment is required at the time of booking to secure your inspection appointment."
    },
    {
      question: "Can I get a refund if I cancel?",
      answer: "If you need to cancel your inspection, the payment will be credited towards a future CarLens inspection. Credits do not expire and can be used anytime you need our services."
    },
    {
      question: "What happens after the inspection?",
      answer: "You'll receive your comprehensive digital report immediately after the technician completes the inspection. The report is sent via email and includes detailed findings, photos, inspector notes, and a clear assessment of the vehicle's condition. You can view and download the report anytime."
    },
    {
      question: "Do you provide pre-purchase advice?",
      answer: "Yes! Pre-purchase advice is a core part of what we do. Beyond the inspection report, our automotive professionals provide unbiased guidance to help you make a smart buying decision. Every Vehicle Decision Report™ includes a clear CarLens Confidence Score™, ownership guidance, and a 24-month ownership cost forecast—so you always know exactly where you stand before you commit."
    },
    {
      question: "Can you inspect vehicles at dealerships?",
      answer: "Yes! We can perform inspections at dealerships, private seller locations, or any agreed-upon location within our service area. Just provide the address when booking."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #141416 0%, #1f1f23 50%, #161618 100%)' }}>
      <Seo siteName="CarLens" title="Vehicle Inspection FAQ | CarLens" description="Answers to common questions about CarLens pre-purchase vehicle inspections — pricing, service areas, reports, and how our independent mobile process works." path="/faq" />
      {/* Header */}
      <header className="border-b border-zinc-800 backdrop-blur-sm bg-black/30">
        <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_5a750074-0ac9-4c5b-a0c0-950bd565c9c5/artifacts/advll02n_ChatGPT%20Image%20Jul%2011%2C%202026%2C%2004_01_30%20PM.png" 
              alt="CarLens" 
              className="h-16 w-auto max-w-[220px] sm:h-24 md:h-36 md:max-w-none cursor-pointer object-contain"
              onClick={() => navigate('/')}
            />
          </div>
          <nav className="flex items-center gap-6">
          </nav>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <div className="inline-block mb-6">
            <div className="px-4 py-2 rounded-full border border-orange-500/30 bg-orange-500/10 text-orange-400 text-sm font-medium">
              Frequently Asked Questions
            </div>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Space Grotesk' }}>
            FAQ
          </h1>
          <p className="text-zinc-400 text-lg">
            Everything you need to know about CarLens inspections
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-xl border border-zinc-800 overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex justify-between items-center text-left hover:bg-white/5 transition-colors"
                data-testid={`faq-question-${index}`}
              >
                <span className="text-lg font-semibold text-white pr-4">
                  {faq.question}
                </span>
                {openIndex === index ? (
                  <ChevronUp className="w-5 h-5 text-orange-500 flex-shrink-0" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-orange-500 flex-shrink-0" />
                )}
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5">
                  <p className="text-zinc-300 leading-relaxed" data-testid={`faq-answer-${index}`}>
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-16 bg-gradient-to-r from-orange-600/20 to-orange-500/20 rounded-3xl border border-orange-500/30 p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Space Grotesk' }}>
            Still Have Questions?
          </h2>
          <p className="text-zinc-300 text-lg mb-6">
            Contact us and we'll be happy to help
          </p>
          <Button 
            size="lg"
            className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg"
            onClick={() => navigate('/contact')}
            data-testid="contact-us-btn"
          >
            Contact Us
          </Button>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-24">
        <div className="max-w-7xl mx-auto px-6 py-8 text-center text-zinc-500">
          <p>© 2025 CarLens Inspections. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default FAQ;
